import { IconPhoneFilled, IconPhoneMissed } from '@iwoca/icons';
import { Trans } from '@lingui/macro';
import clsx from 'clsx';
import { useLocale } from 'hooks/useLocale';
import { useGetCustomerContactUpdates } from 'lendingApi/lendingApiHooks';
import Skeleton from 'react-loading-skeleton';
import { Tooltip } from 'react-tooltip';
import styles from './ContactUpdates.module.css';
import 'react-loading-skeleton/dist/skeleton.css';

export const ContactUpdates = () => {
  const { locale } = useLocale();
  const { data: customerContactUpdateData, isLoading } =
    useGetCustomerContactUpdates();
  const updates = customerContactUpdateData?.data;

  const formatDate = (timestamp?: string) => {
    if (!timestamp) {
      return 'N/A';
    }

    const date = new Date(timestamp);

    return new Intl.DateTimeFormat(locale, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })
      .format(date)
      .replace(',', ' -');
  };

  const renderTooltip = () => {
    const isCallSuccessful = updates?.last_call_data?.successful;

    return (
      <div
        data-testid="phone-tooltip-trigger"
        data-tooltip-id="phone-tooltip"
        className={styles.iconContainer}
      >
        {isCallSuccessful ? (
          <IconPhoneFilled className={clsx(styles.icon, styles.successful)} />
        ) : (
          <IconPhoneMissed className={clsx(styles.icon)} />
        )}
        <Tooltip
          className={styles.tooltip}
          id="phone-tooltip"
          opacity={1}
          openEvents={{ focus: false, mouseenter: true }}
          border="1px solid var(--color-structure-80)"
          place="top"
        >
          {isCallSuccessful ? (
            <Trans>Successful call</Trans>
          ) : (
            <Trans>Unsuccessful call</Trans>
          )}
        </Tooltip>
      </div>
    );
  };

  const renderContent = () => {
    if (!updates || isLoading) {
      return <Skeleton className={styles.spinner} width={180} height={24} />;
    }

    return (
      <>
        <div className={styles.text}>
          <span className={styles.label}>
            <Trans>Customer logged in:</Trans>
          </span>
          <span className={styles.update}>
            {formatDate(updates?.customer_last_active)}
          </span>
        </div>
        <div className={styles.text}>
          <span className={styles.label}>
            <Trans>iwoca emailed:</Trans>
          </span>
          <span className={styles.update}>
            {formatDate(updates?.last_email_sent)}
          </span>
        </div>
        <div className={styles.text}>
          <span className={styles.label}>
            <Trans>iwoca texted:</Trans>
          </span>
          <span className={styles.update}>
            {formatDate(updates?.last_text_sent)}
          </span>
        </div>
        <div className={styles.text}>
          <span className={styles.label}>
            <Trans>iwoca called:</Trans>
          </span>
          <span className={styles.update}>
            {formatDate(updates?.last_call_data?.call_time)}
            {updates?.last_call_data?.call_time && renderTooltip()}
          </span>
        </div>
      </>
    );
  };

  return (
    <div className={styles.container}>
      <h2 className={styles.heading}>
        <Trans>Latest customer contact:</Trans>
      </h2>
      {renderContent()}
    </div>
  );
};
